import { supabase } from '../lib/supabase';
import { activityService } from './activityService';
import { notificationService } from './notificationService';
import { DEMO_ERROR_MESSAGE, isDemoUser } from '../utils/demoMode';

export const snagService = {
  async createSnag(snagData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Demo mode guard — surface an explicit error instead of fake success.
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }

      const { data, error } = await supabase
        ?.from('snags')
        ?.insert({
          company_id: snagData?.companyId,
          site_id: snagData?.siteId,
          title: snagData?.title,
          description: snagData?.description,
          location: snagData?.location || null,
          status: snagData?.status || 'open',
          priority: snagData?.priority || 'medium',
          assigned_to: snagData?.assignedTo || null,
          photo_paths: snagData?.photoPaths || [],
          drawing_id: snagData?.drawingId || null,
          x_position: snagData?.xPosition ?? null,
          y_position: snagData?.yPosition ?? null,
          due_date: snagData?.dueDate || null,
          created_by: user?.id
        })
        ?.select('*, sites(site_name), user_profiles!snags_created_by_fkey(full_name), assigned_user:user_profiles!snags_assigned_to_fkey(id,full_name,email)')
        ?.single();

      if (error) throw error;

      // Log activity
      await activityService?.createActivity({
        companyId: snagData?.companyId,
        siteId: snagData?.siteId,
        activityType: 'snag_created',
        title: `Snag raised: ${snagData?.title}`,
        description: `Priority: ${snagData?.priority || 'medium'} | Status: open | ${snagData?.location ? 'Location: ' + snagData?.location : ''}`,
        severityLevel: snagData?.priority === 'high' ? 'high' : snagData?.priority === 'low' ? 'low' : 'medium',
        relatedEntityId: data?.id,
        metadata: { snagId: data?.id, priority: snagData?.priority, location: snagData?.location }
      });

      // Send assignment email if snag has an assignee
      if (data?.assigned_user?.email) {
        const creatorProfile = await supabase
          ?.from('user_profiles')
          ?.select('full_name')
          ?.eq('id', user?.id)
          ?.maybeSingle();
        notificationService?.sendSnagAssigned({
          snagId: data?.id,
          assigneeEmail: data?.assigned_user?.email,
          assigneeName: data?.assigned_user?.full_name,
          assigneeUserId: data?.assigned_user?.id,
          assignerName: creatorProfile?.data?.full_name || 'A team member',
          snagTitle: data?.title,
          snagDescription: data?.description || '',
          severity: data?.priority || 'medium',
          siteName: data?.sites?.site_name || '',
          dueDate: data?.due_date || '',
        })?.catch(() => {}); // fire-and-forget
      }

      // In-app notification for snag raised — notify assigned user
      if (data?.assigned_to) {
        notificationService?.createNotification(
          data?.assigned_to,
          'snag_raised',
          'Snag Raised',
          `A snag has been raised: "${data?.title}"${data?.sites?.site_name ? ' on ' + data?.sites?.site_name : ''}`,
          `/snags/${data?.id}`,
          data?.id,
          data?.site_id
        )?.catch(() => {});
      }

      // Notify all admins that a new issue was logged
      notificationService?.notifyNewIssueLogged(
        data?.id,
        data?.title,
        snagData?.companyId,
        snagData?.siteId,
        data?.user_profiles?.full_name || null
      )?.catch(() => {});

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // NSherjFh PR 4 — default-filters soft-deleted snags. Pass
  // `{ includeDeleted: true }` from the snags-list 'View deleted' tab.
  // Identical opt-in shape to PR 1's includeArchived, PR 2's
  // includeDeleted on documents, and PR 3's on rams_documents.
  // NB: the `status` enum (open / in_progress / resolved / closed) is
  // a DIFFERENT axis from deleted_at — a closed snag is still active
  // (just resolved). Do NOT filter on status here.
  async getSnagsBySite(siteId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('snags')
        ?.select('*, sites(site_name), user_profiles!snags_created_by_fkey(full_name), assigned_user:user_profiles!snags_assigned_to_fkey(full_name)')
        ?.eq('site_id', siteId)
        ?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getSnagsByDrawing(drawingId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('snags')
        ?.select('*, sites(site_name), user_profiles!snags_created_by_fkey(full_name), assigned_user:user_profiles!snags_assigned_to_fkey(full_name)')
        ?.eq('drawing_id', drawingId)
        ?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getAllSnags(options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('snags')
        ?.select('*, sites(site_name), user_profiles!snags_created_by_fkey(full_name), assigned_user:user_profiles!snags_assigned_to_fkey(full_name)')
        ?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getSnagById(snagId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('snags')
        ?.select('*, sites(site_name, address_line1, city), user_profiles!snags_created_by_fkey(full_name, email), assigned_user:user_profiles!snags_assigned_to_fkey(full_name, email), resolver:user_profiles!snags_resolved_by_fkey(full_name)')
        ?.eq('id', snagId);
      if (!includeDeleted) query = query?.is('deleted_at', null);
      // maybeSingle so a soft-deleted id returns null gracefully (the
      // snag-detail page can render a not-found state) instead of
      // throwing PGRST116. Matches PR 2's documentService.getDocumentById.
      const { data, error } = await query?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async updateSnag(snagId, updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Demo mode guard — surface an explicit error instead of fake success.
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }

      const updatePayload = {};
      if (updates?.title !== undefined) updatePayload.title = updates?.title;
      if (updates?.description !== undefined) updatePayload.description = updates?.description;
      if (updates?.location !== undefined) updatePayload.location = updates?.location;
      if (updates?.priority !== undefined) updatePayload.priority = updates?.priority;
      if (updates?.assignedTo !== undefined) updatePayload.assigned_to = updates?.assignedTo;
      if (updates?.photoPaths !== undefined) updatePayload.photo_paths = updates?.photoPaths;
      if (updates?.resolutionNotes !== undefined) updatePayload.resolution_notes = updates?.resolutionNotes;

      if (updates?.status !== undefined) {
        updatePayload.status = updates?.status;
        if (updates?.status === 'resolved') {
          updatePayload.resolved_at = new Date()?.toISOString();
          updatePayload.resolved_by = user?.id;
        }
      }

      const { data, error } = await supabase
        ?.from('snags')
        ?.update(updatePayload)
        ?.eq('id', snagId)
        ?.select('*, sites(site_name), user_profiles!snags_created_by_fkey(id,full_name), assigned_user:user_profiles!snags_assigned_to_fkey(id,full_name,email)')
        ?.single();

      if (error) throw error;

      // Log activity
      const activityType = updates?.status === 'resolved' ? 'snag_resolved' : 'snag_updated';
      await activityService?.createActivity({
        companyId: data?.company_id,
        siteId: data?.site_id,
        activityType,
        title: updates?.status === 'resolved'
          ? `Snag resolved: ${data?.title}`
          : `Snag updated: ${data?.title}`,
        description: updates?.status
          ? `Status changed to: ${updates?.status}`
          : 'Snag details updated',
        severityLevel: data?.priority === 'high' ? 'high' : data?.priority === 'low' ? 'low' : 'medium',
        relatedEntityId: data?.id,
        metadata: { snagId: data?.id, priority: data?.priority, status: updates?.status }
      });

      // Send assignment email if assignedTo changed
      if (updates?.assignedTo !== undefined && data?.assigned_user?.email) {
        const creatorProfile = await supabase
          ?.from('user_profiles')
          ?.select('full_name')
          ?.eq('id', user?.id)
          ?.maybeSingle();
        notificationService?.sendSnagAssigned({
          snagId: data?.id,
          assigneeEmail: data?.assigned_user?.email,
          assigneeName: data?.assigned_user?.full_name,
          assigneeUserId: data?.assigned_user?.id,
          assignerName: creatorProfile?.data?.full_name || 'A team member',
          snagTitle: data?.title,
          snagDescription: data?.description || '',
          severity: data?.priority || 'medium',
          siteName: data?.sites?.site_name || '',
          dueDate: data?.due_date || '',
        })?.catch(() => {}); // fire-and-forget
      }

      // Send status changed email if status changed
      if (updates?.status !== undefined) {
        notificationService?.sendSnagStatusChanged({
          snagId: data?.id,
          title: data?.title,
          site: data?.sites?.site_name || '',
          status: updates?.status,
          severity: data?.priority || '',
          assignedTo: data?.assigned_user?.id || null,
          creator: data?.user_profiles?.id || null,
        })?.catch(() => {}); // fire-and-forget
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async updateSnagStatus(snagId, newStatus) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const updatePayload = { status: newStatus };
      if (newStatus === 'resolved' || newStatus === 'completed') {
        updatePayload.resolved_at = new Date()?.toISOString();
        updatePayload.resolved_by = user?.id;
      }

      const { data, error } = await supabase
        ?.from('snags')
        ?.update(updatePayload)
        ?.eq('id', snagId)
        ?.select('*, sites(site_name), user_profiles!snags_created_by_fkey(id,full_name), assigned_user:user_profiles!snags_assigned_to_fkey(id,full_name,email)')
        ?.single();

      if (error) throw error;

      // Send status changed email notification
      notificationService?.sendSnagStatusChanged({
        snagId: data?.id,
        title: data?.title,
        site: data?.sites?.site_name || '',
        status: newStatus,
        severity: data?.priority || '',
        assignedTo: data?.assigned_user?.id || null,
        creator: data?.user_profiles?.id || null,
      })?.catch(() => {}); // fire-and-forget

      // Notify admins when an issue is closed/resolved
      if (newStatus === 'resolved' || newStatus === 'completed') {
        const resolverProfile = await supabase
          ?.from('user_profiles')
          ?.select('full_name, company_id')
          ?.eq('id', user?.id)
          ?.maybeSingle();
        notificationService?.notifyIssueClosed(
          data?.id,
          data?.title,
          resolverProfile?.data?.company_id || data?.company_id,
          data?.site_id,
          resolverProfile?.data?.full_name || null
        )?.catch(() => {});
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // NSherjFh PR 4 — recoverable soft-delete. Previously this was a
  // hard DELETE called by no UI; now it stamps deleted_at + deleted_by
  // and is wired up to the new admin Delete button in snags-list-
  // creation. Idempotent via `.is('deleted_at', null)`: a second
  // click is a no-op (returns null data, no error) and preserves the
  // original deleter attribution.
  //
  // snag_comments rows on the deleted snag remain in the DB — they're
  // a historical conversation thread. They become unreachable from
  // active surfaces because getSnagComments is only called from the
  // detail page, which loads the parent via getSnagById (now default-
  // filtered), so a soft-deleted parent never resolves and the page
  // never asks for comments.
  //
  // Site-mutable trigger note: trg_enforce_site_mutable fires BEFORE
  // INSERT/UPDATE on snags and blocks UPDATEs when the parent site is
  // `completed` or `archived`. That means soft-delete + restore are
  // ALSO blocked on read-only sites, with a clear 'Site is read-only'
  // error. Pre-existing umbrella-wide constraint, tracked separately.
  async deleteSnag(snagId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      // Demo mode guard — surface an explicit error instead of fake success.
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }
      const { data, error } = await supabase
        ?.from('snags')
        ?.update({
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id,
        })
        ?.eq('id', snagId)
        ?.is('deleted_at', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Inverse of deleteSnag. snag_comments were never touched, so
  // restore brings the comment thread back automatically.
  async restoreSnag(snagId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }
      const { data, error } = await supabase
        ?.from('snags')
        ?.update({
          deleted_at: null,
          deleted_by: null,
        })
        ?.eq('id', snagId)
        ?.not('deleted_at', 'is', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async uploadSnagPhoto(file, companyId, siteId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${companyId}/${siteId}/${fileName}`;

      const { error: uploadError } = await supabase?.storage
        ?.from('snag-photos')
        ?.upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;
      return { filePath, error: null };
    } catch (error) {
      return { filePath: null, error: { message: error?.message } };
    }
  },

  async getSnagPhotoUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('snag-photos')
        ?.createSignedUrl(filePath, 3600);

      if (error) throw error;
      return { url: data?.signedUrl, error: null };
    } catch (error) {
      return { url: null, error: { message: error?.message } };
    }
  },

  async getSnagComments(snagId) {
    try {
      const { data, error } = await supabase
        ?.from('snag_comments')
        ?.select('*, user_profiles!snag_comments_user_id_fkey(full_name, avatar_url)')
        ?.eq('snag_id', snagId)
        ?.order('created_at', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async addComment(snagId, companyId, comment) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('snag_comments')
        ?.insert({
          snag_id: snagId,
          company_id: companyId,
          user_id: user?.id,
          comment
        })
        ?.select('*, user_profiles!snag_comments_user_id_fkey(full_name, avatar_url)')
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  }
};
